import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  CheckCircle,
  XCircle,
  Clock,
  ArrowsClockwise,
  CaretDown,
  CaretUp,
  Warning,
  Package,
} from '@phosphor-icons/react';
import {
  apiClient,
  API_ENDPOINTS,
  Button,
  Spinner,
  useAuthStore,
  useNotificationStore,
} from '@dukene/shared';

interface SyncIssue {
  productId: string;
  title: string;
  error: string;
  lastAttemptAt: string;
}

interface MetaSyncStatusResponse {
  isConnected: boolean;
  metaCatalogId: string | null;
  metaBusinessId: string | null;
  syncStatus: string;
  lastSyncedAt?: string | null;
  hasLegacySyncHistory?: boolean;
  stats: {
    total: number;
    synced: number;
    pending: number;
    failed: number;
    excluded?: number;
  };
  issues?: SyncIssue[];
}

export function MetaSyncStatusPanel() {
  const queryClient = useQueryClient();
  const activeStoreId = useAuthStore((state) => state.activeStoreId);
  const { addNotification } = useNotificationStore();
  const [issuesExpanded, setIssuesExpanded] = useState(false);

  const { data: status, isLoading } = useQuery<MetaSyncStatusResponse>({
    queryKey: ['meta-sync-status', activeStoreId],
    queryFn: () => apiClient.get<MetaSyncStatusResponse>(API_ENDPOINTS.META.SYNC_STATUS),
    enabled: !!activeStoreId,
    refetchInterval: 15000,
  });

  const syncNowMutation = useMutation({
    mutationFn: () => apiClient.post(API_ENDPOINTS.META.SYNC_PRODUCTS),
    onSuccess: () => {
      addNotification({
        type: 'success',
        title: 'Sync Started',
        message: 'Product synchronization with Meta Commerce has started.',
      });
      queryClient.invalidateQueries({ queryKey: ['meta-sync-status', activeStoreId] });
    },
    onError: (err: any) => {
      addNotification({
        type: 'error',
        title: 'Sync Failed',
        message: err?.response?.data?.error || err?.message || 'Failed to start catalog sync.',
      });
    },
  });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center p-6 bg-[#F6F6F7] dark:bg-slate-900 rounded-xl border border-[#D2D5D9] dark:border-slate-800">
        <Spinner size="sm" className="mr-2" />
        <span className="text-xs text-[#5C5F62] dark:text-slate-400">Loading Meta catalog sync status…</span>
      </div>
    );
  }

  const stats = status?.stats || { total: 0, synced: 0, pending: 0, failed: 0 };
  const issues = status?.issues || [];
  const hasIssues = issues.length > 0;
  const isSyncing = syncNowMutation.isPending || status?.syncStatus === 'SYNCING';

  return (
    <div className="space-y-4">
      {/* Top action row */}
      <div className="flex items-center justify-between pb-2 border-b border-[#E1E3E5] dark:border-slate-800">
        <div>
          <h3 className="text-sm font-bold text-[#202223] dark:text-white">Catalog Synchronization</h3>
          <p className="text-xs text-[#5C5F62] dark:text-slate-400 mt-0.5">
            Real-time status of your store catalog synced to your Meta Commerce catalog
          </p>
        </div>
        <Button
          onClick={() => syncNowMutation.mutate()}
          disabled={isSyncing || !status?.metaCatalogId}
          title={!status?.metaCatalogId ? 'Please select or create a Meta catalog first' : ''}
          variant="outline"
          className="border-[#D2D5D9] dark:border-slate-700 hover:border-[#202223] dark:hover:border-slate-400 h-8 px-3 rounded-lg text-xs font-semibold"
        >
          <ArrowsClockwise weight="bold" className={`w-3.5 h-3.5 mr-1.5 ${isSyncing ? 'animate-spin' : ''}`} />
          {isSyncing ? 'Syncing…' : 'Sync Now'}
        </Button>
      </div>

      {/* 4 Stat Cards */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {/* Total */}
        <div className="bg-white dark:bg-[#1A1D21] rounded-xl p-3.5 border border-[#D2D5D9] dark:border-slate-800 shadow-2xs">
          <div className="flex items-center justify-between mb-1.5">
            <span className="text-[10px] font-bold uppercase tracking-wider text-[#5C5F62]">Total</span>
            <Package weight="fill" className="w-3.5 h-3.5 text-[#5C5F62]" />
          </div>
          <p className="text-xl font-bold text-[#202223] dark:text-white">{stats.total}</p>
          <p className="text-[11px] text-[#5C5F62] mt-0.5">Active products</p>
        </div>

        {/* Synced */}
        <div className="bg-white dark:bg-[#1A1D21] rounded-xl p-3.5 border border-[#D2D5D9] dark:border-slate-800 shadow-2xs">
          <div className="flex items-center justify-between mb-1.5">
            <span className="text-[10px] font-bold uppercase tracking-wider text-emerald-700 dark:text-emerald-400">Synced</span>
            <CheckCircle weight="fill" className="w-3.5 h-3.5 text-emerald-600" />
          </div>
          <p className="text-xl font-bold text-emerald-800 dark:text-emerald-300">{stats.synced}</p>
          <p className="text-[11px] text-emerald-600 dark:text-emerald-400 mt-0.5">Live on Meta</p>
        </div>

        {/* Pending */}
        <div className="bg-white dark:bg-[#1A1D21] rounded-xl p-3.5 border border-[#D2D5D9] dark:border-slate-800 shadow-2xs">
          <div className="flex items-center justify-between mb-1.5">
            <span className="text-[10px] font-bold uppercase tracking-wider text-amber-700 dark:text-amber-400">Pending</span>
            <Clock weight="fill" className="w-3.5 h-3.5 text-amber-600" />
          </div>
          <p className="text-xl font-bold text-amber-800 dark:text-amber-300">{stats.pending}</p>
          <p className="text-[11px] text-amber-600 dark:text-amber-400 mt-0.5">Awaiting sync</p>
        </div>

        {/* Issues / Failed */}
        <div className="bg-white dark:bg-[#1A1D21] rounded-xl p-3.5 border border-[#D2D5D9] dark:border-slate-800 shadow-2xs">
          <div className="flex items-center justify-between mb-1.5">
            <span className="text-[10px] font-bold uppercase tracking-wider text-red-700 dark:text-red-400">Issues</span>
            <XCircle weight="fill" className="w-3.5 h-3.5 text-red-600" />
          </div>
          <p className="text-xl font-bold text-red-800 dark:text-red-300">{stats.failed}</p>
          <p className="text-[11px] text-red-600 dark:text-red-400 mt-0.5">Needs attention</p>
        </div>
      </div>

      {/* Expandable Issues Accordion */}
      {hasIssues && (
        <div className="border border-red-200 dark:border-red-900/60 rounded-xl overflow-hidden">
          <button
            type="button"
            onClick={() => setIssuesExpanded(!issuesExpanded)}
            className="w-full px-4 py-3 bg-red-50 dark:bg-red-950/40 flex items-center justify-between text-left hover:bg-red-100/60 dark:hover:bg-red-950/60 transition-colors"
          >
            <div className="flex items-center gap-2">
              <Warning weight="fill" className="w-4 h-4 text-red-600 shrink-0" />
              <span className="font-bold text-xs text-red-900 dark:text-red-300">
                Sync Issues ({issues.length} {issues.length === 1 ? 'product' : 'products'} requiring attention)
              </span>
            </div>
            {issuesExpanded ? (
              <CaretUp weight="bold" className="w-4 h-4 text-red-700" />
            ) : (
              <CaretDown weight="bold" className="w-4 h-4 text-red-700" />
            )}
          </button>

          {issuesExpanded && (
            <div className="divide-y divide-red-100 dark:divide-red-900/40 bg-white dark:bg-slate-900">
              {issues.map((issue) => (
                <div key={issue.productId} className="px-4 py-3 text-xs">
                  <div className="flex items-center justify-between mb-1">
                    <span className="font-semibold text-[#202223] dark:text-white">{issue.title}</span>
                    {issue.lastAttemptAt && (
                      <span className="text-[11px] text-[#5C5F62]">
                        {new Date(issue.lastAttemptAt).toLocaleTimeString()}
                      </span>
                    )}
                  </div>
                  <p className="text-red-700 dark:text-red-400">{issue.error}</p>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
